const { cloudinaryDeliveryUrl, cloudinarySrcSet, cloudinaryBestLimit, cloudinaryBestSrcSet, journalListDelivery } = require('./imageSeo');
const sitePaths = require('./sitePaths');

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function plain(value, max = 1000) {
  let text = String(value || '').replace(/<[^>]+>/g, ' ');
  for (let pass = 0; pass < 2; pass += 1) {
    text = text
      .replace(/&amp;/gi, '&')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'");
  }
  text = text.replace(/\s+/g, ' ').trim();
  if (!max || text.length <= max) return text;
  return text.slice(0, max).trim();
}

function attr(source, name) {
  const match = String(source || '').match(new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'i'));
  return match ? (match[2] || match[3] || '') : '';
}

function responsiveImage(image, options = {}) {
  const url = image && (image.secure_url || image.url) || '';
  if (!url) return '';
  const width = Number(image.width) || 1600;
  const height = Number(image.height) || 1067;
  const src = cloudinaryDeliveryUrl(url, Math.min(width, 1600)) || url;
  const set = cloudinarySrcSet(url, [480, 768, 1200, 1600]);
  const loading = options.lazy === false ? 'eager' : 'lazy';
  const priority = options.lazy === false ? ' fetchpriority="high"' : '';
  const dims = width && height ? ` width="${Math.round(width)}" height="${Math.round(height)}"` : '';
  const srcset = set ? ` srcset="${escapeHtml(set)}" sizes="${escapeHtml(options.sizes || '(max-width: 720px) 100vw, 720px')}"` : '';
  return `<img src="${escapeHtml(src)}" alt="${escapeHtml(image.alt || '')}"${dims}${srcset} loading="${loading}"${priority} decoding="async">`;
}

const GALLERY_CARD_LEAD = {
  width: 1600,
  sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(27rem, 38vw)',
};

const GALLERY_CARD_REST = {
  width: 1200,
  sizes: '(max-width: 720px) calc(50vw - 0.9rem), min(22.5rem, 31vw)',
};

function galleryCardWidths(maxWidth) {
  return [480, 720, 960, 1200, 1600].filter((width) => width < maxWidth).concat(maxWidth);
}

function galleryCardDelivery(url, lead) {
  const slot = lead ? GALLERY_CARD_LEAD : GALLERY_CARD_REST;
  return {
    src: cloudinaryBestLimit(url, slot.width) || String(url || ''),
    srcSet: cloudinaryBestSrcSet(url, galleryCardWidths(slot.width)),
    sizes: slot.sizes,
  };
}

function journalListImage(image) {
  const url = image && (image.secure_url || image.url) || '';
  if (!url) return '';
  const delivery = journalListDelivery(url, image);
  const srcset = delivery.srcSet
    ? ` srcset="${escapeHtml(delivery.srcSet)}" sizes="${escapeHtml(delivery.sizes)}"`
    : '';
  return `<img src="${escapeHtml(delivery.src)}" alt="${escapeHtml(image.alt || '')}" width="${delivery.width}" height="${delivery.height}"${srcset} loading="lazy" decoding="async">`;
}

function galleryCardImage(image, options = {}) {
  const url = image && (image.secure_url || image.url) || '';
  if (!url) return '';
  const delivery = galleryCardDelivery(url, Boolean(options.lead));
  const srcset = delivery.srcSet
    ? ` srcset="${escapeHtml(delivery.srcSet)}" sizes="${escapeHtml(delivery.sizes)}"`
    : '';
  const naturalWidth = Number(image && image.width) || 0;
  const naturalHeight = Number(image && image.height) || 0;
  const dims = naturalWidth > 0 && naturalHeight > 0
    ? ` width="${Math.round(naturalWidth)}" height="${Math.round(naturalHeight)}"`
    : '';
  return `<img src="${escapeHtml(delivery.src)}" alt="${escapeHtml(image.alt || '')}"${dims}${srcset} loading="lazy" decoding="async">`;
}

// A single figure is at most 60rem wide. Two- and three-photo rows use the full
// 72rem article, with a 1rem gap. Each photograph keeps its own shape.
const INLINE_SIZES = '(max-width: 720px) calc(100vw - 1.5rem), min(60rem, calc(100vw - 2.5rem))';
const INLINE_WIDTHS = [640, 960, 1280, 1600, 1920, 2400];
const ROW_SLOTS = {
  '2': [
    { cssWidth: 697, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(43.568rem, calc((100vw - 3.5rem) * 0.6136))' },
    { cssWidth: 439, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(27.432rem, calc((100vw - 3.5rem) * 0.3864))' },
  ],
  '2-equal': [
    { cssWidth: 568, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(35.5rem, calc((100vw - 3.5rem) / 2))' },
    { cssWidth: 568, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(35.5rem, calc((100vw - 3.5rem) / 2))' },
  ],
  '3': [
    { cssWidth: 326, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(20.364rem, calc((100vw - 4.5rem) * 0.2909))' },
    { cssWidth: 468, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(29.273rem, calc((100vw - 4.5rem) * 0.4182))' },
    { cssWidth: 326, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(20.364rem, calc((100vw - 4.5rem) * 0.2909))' },
  ],
  '3-equal': [
    { cssWidth: 373, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(23.333rem, calc((100vw - 4.5rem) / 3))' },
    { cssWidth: 373, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(23.333rem, calc((100vw - 4.5rem) / 3))' },
    { cssWidth: 373, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(23.333rem, calc((100vw - 4.5rem) / 3))' },
  ],
};

function rowSlots(columns, equal) {
  if (columns === 3) return equal ? ROW_SLOTS['3-equal'] : ROW_SLOTS['3'];
  return equal ? ROW_SLOTS['2-equal'] : ROW_SLOTS['2'];
}

const MASONRY_SLOTS = {
  2: { cssWidth: 568, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(35.5rem, calc((100vw - 3.5rem) / 2))' },
  3: { cssWidth: 373, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(23.333rem, calc((100vw - 4.5rem) / 3))' },
  4: { cssWidth: 277, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(17.25rem, calc((100vw - 5.5rem) / 4))' },
  5: { cssWidth: 218, sizes: '(max-width: 720px) calc(100vw - 1.5rem), min(13.6rem, calc((100vw - 6.5rem) / 5))' },
};

function masonryColumns(className) {
  if (/\bphoto-masonry-col\b/.test(className) || !/\bphoto-masonry\b/.test(className)) return 0;
  const match = String(className || '').match(/\bphoto-masonry-([1-5])\b/);
  if (match) return Number(match[1]);
  return 3;
}

function openMasonry(stack) {
  for (let index = stack.length - 1; index >= 0; index -= 1) {
    if (stack[index] && stack[index].kind === 'masonry') return stack[index];
  }
  return null;
}

function openRow(stack) {
  for (let index = stack.length - 1; index >= 0; index -= 1) {
    if (stack[index] && stack[index].kind === 'row') return stack[index];
  }
  return null;
}

function deliveryWidths(cssWidth) {
  const needed = Math.min(3200, Math.max(480, Math.round(cssWidth * 2)));
  return [480, 720, 960, 1200, 1600, 2000, 2400, 2800, 3200].filter((width) => width < needed).concat(needed);
}

function deliveredImage(src, alt, width, height, widths, sizes) {
  const url = cloudinaryBestLimit(src, widths[widths.length - 1]) || src;
  const set = cloudinaryBestSrcSet(src, widths);
  const srcset = set ? ` srcset="${escapeHtml(set)}" sizes="${escapeHtml(sizes)}"` : '';
  const dims = width && height ? ` width="${escapeHtml(width)}" height="${escapeHtml(height)}"` : '';
  return `<img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}"${dims}${srcset} loading="lazy" decoding="async">`;
}

function imageFromAttrs(attrs, slot) {
  const src = attr(attrs, 'src');
  const alt = attr(attrs, 'alt');
  if (!src || !/^https:\/\//i.test(src) || !plain(alt)) return '';
  const width = attr(attrs, 'width');
  const height = attr(attrs, 'height');
  if (slot) {
    return deliveredImage(src, alt, width, height, deliveryWidths(slot.cssWidth), slot.sizes);
  }
  return deliveredImage(src, alt, width, height, INLINE_WIDTHS, INLINE_SIZES);
}

function logoImage(attrs) {
  const src = attr(attrs, 'src');
  if (!src || !/^https:\/\//i.test(src)) return '';
  const alt = plain(attr(attrs, 'alt'), 180) || 'Carmen Lorraine Photography';
  const width = Number(attr(attrs, 'width'));
  const height = Number(attr(attrs, 'height'));
  const dims = width > 0 && height > 0 ? ` width="${Math.round(width)}" height="${Math.round(height)}"` : '';
  const delivered = cloudinaryDeliveryUrl(src, 320) || src;
  const set = cloudinarySrcSet(src, [160, 320]);
  const srcset = set ? ` srcset="${escapeHtml(set)}" sizes="8rem"` : '';
  return `<img src="${escapeHtml(delivered)}" alt="${escapeHtml(alt)}"${dims}${srcset} loading="lazy" decoding="async">`;
}

function logoSize(className) {
  if (/\bblog-logo-lg\b/.test(className)) return 'blog-logo-lg';
  if (/\bblog-logo-sm\b/.test(className)) return 'blog-logo-sm';
  return 'blog-logo-md';
}

function addHeadingIds(html) {
  const seen = new Map();
  return String(html || '').replace(/<h2>([\s\S]*?)<\/h2>/gi, (full, inner) => {
    const text = plain(inner, 140);
    const base = sitePaths.slugify(text) || 'section';
    const count = (seen.get(base) || 0) + 1;
    seen.set(base, count);
    const id = count === 1 ? base : `${base}-${count}`;
    return `<h2 id="${id}">${inner}</h2>`;
  });
}

function articleHeadings(html) {
  const items = [];
  String(html || '').replace(/<h2\b[^>]*\bid="([^"]+)"[^>]*>([\s\S]*?)<\/h2>/gi, (full, id, inner) => {
    const text = plain(inner, 140);
    if (id && text) items.push({ id, text });
    return full;
  });
  return items;
}

function nextTag(html, pattern, from) {
  pattern.lastIndex = from;
  const match = pattern.exec(html);
  return match ? match.index : -1;
}

function findClose(html, tag, start) {
  const open = new RegExp(`<${tag}\\b[^>]*>`, 'gi');
  const close = new RegExp(`</${tag}>`, 'gi');
  let depth = 1;
  let cursor = start + 1;
  while (depth > 0) {
    const openAt = nextTag(html, open, cursor);
    const closeAt = nextTag(html, close, cursor);
    if (closeAt < 0) return -1;
    if (openAt >= 0 && openAt < closeAt) {
      depth += 1;
      cursor = openAt + 1;
    } else {
      depth -= 1;
      cursor = closeAt + tag.length + 3;
      if (depth === 0) return cursor;
    }
  }
  return -1;
}

function paragraphize(html) {
  const input = String(html || '');
  const opener = /<(h2|h3|ul|ol|blockquote|figure|div|p)\b[^>]*>/gi;
  const parts = [];
  let cursor = 0;
  let match = opener.exec(input);
  while (match) {
    if (match.index > cursor) parts.push(input.slice(cursor, match.index));
    const tag = match[1].toLowerCase();
    const end = findClose(input, tag, match.index);
    if (end < 0) {
      parts.push(input.slice(match.index));
      cursor = input.length;
      break;
    }
    parts.push(input.slice(match.index, end));
    cursor = end;
    opener.lastIndex = end;
    match = opener.exec(input);
  }
  if (cursor < input.length) parts.push(input.slice(cursor));
  return parts.map((part) => {
    if (/^<(?:h2|h3|ul|ol|blockquote|figure|div|p)\b/i.test(part.trim())) return part;
    return part
      .split(/<br\s*\/?>/gi)
      .map((piece) => piece.trim())
      .filter((piece) => piece && piece !== '&nbsp;')
      .map((piece) => `<p>${piece}</p>`)
      .join('');
  }).join('');
}

function normalizeEditorHtml(value) {
  let html = String(value || '')
    .replace(/<i\b[^>]*>/gi, '<em>')
    .replace(/<\/i>/gi, '</em>');
  for (let pass = 0; pass < 6; pass += 1) {
    const next = html.replace(/<div\b([^>]*)>([\s\S]*?)<\/div>/gi, (full, attrs, inner) => {
      const className = attr(attrs, 'class');
      if (/\b(?:photo-row|photo-masonry|blog-rule|blog-logo)\b/.test(className)) return full;
      if (/<(?:div|ul|ol|figure|blockquote|h2|h3|p)\b/i.test(inner)) return inner;
      const pieces = inner.split(/<br\s*\/?>/gi).map((piece) => piece.trim()).filter((piece) => piece && piece !== '&nbsp;');
      if (!pieces.length) return '<p><br></p>';
      return pieces.map((piece) => `<p>${piece}</p>`).join('');
    });
    if (next === html) break;
    html = next;
  }
  return html.trim() || '<p></p>';
}

const ALLOWED = new Set(['p', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'strong', 'em', 'a', 'img', 'figure', 'figcaption', 'br', 'div']);

const RETIRED_PICKER_PATHS = {
  '/family-photographer-cape-town': '/galleries/family',
  '/business-photographer-cape-town': '/galleries/business-and-commercial',
};

function isLocalHost(hostname) {
  const host = String(hostname || '').replace(/^\[|\]$/g, '').toLowerCase();
  return host === '127.0.0.1' || host === 'localhost' || host === '0.0.0.0' || host === '::1';
}

function relativeFromUrl(url) {
  return `${url.pathname || '/'}${url.search || ''}${url.hash || ''}`;
}

function localHrefToRelative(href) {
  const value = String(href || '').trim();
  if (!/^https?:\/\//i.test(value)) return value;
  try {
    const url = new URL(value);
    if (!isLocalHost(url.hostname)) return value;
    return relativeFromUrl(url);
  } catch (error) {
    return value;
  }
}

function normalizeEditorHref(href, origin) {
  const value = String(href || '').trim();
  if (!value || /^(?:#|mailto:|tel:|javascript:)/i.test(value)) return value;
  let url;
  try {
    url = new URL(value, origin || 'https://carmenlorraine.com');
  } catch (error) {
    return value;
  }
  const local = isLocalHost(url.hostname);
  let own = false;
  if (origin) {
    try {
      own = url.origin === new URL(origin).origin;
    } catch (error) {
      own = false;
    }
  }
  if (!local && !own) return value;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  return relativeFromUrl(url);
}

function rewriteStoredHref(href) {
  const relative = localHrefToRelative(href);
  try {
    const url = new URL(relative, 'https://carmenlorraine.com');
    const path = url.pathname.replace(/\/+$/, '') || '/';
    const retired = RETIRED_PICKER_PATHS[path];
    const onThisSite = relative.startsWith('/') || /(^|\.)carmenlorraine\.com$/i.test(url.hostname) || isLocalHost(url.hostname);
    if (retired && onThisSite) return `${retired}${url.search}${url.hash}`;
  } catch (error) {
    return relative;
  }
  return relative;
}

function linkText(html) {
  return String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function rewriteStoredBlogHtml(html) {
  const changes = [];
  const next = String(html || '').replace(/<a\b([^>]*?)>([\s\S]*?)<\/a>/gi, (full, attrs, inner) => {
    const hrefMatch = attrs.match(/\bhref\s*=\s*("([^"]*)"|'([^']*)')/i);
    if (!hrefMatch) return full;
    const href = hrefMatch[2] != null ? hrefMatch[2] : hrefMatch[3];
    const rewritten = rewriteStoredHref(href);
    if (rewritten === href) return full;
    changes.push({ text: linkText(inner), before: href, after: rewritten });
    const quote = hrefMatch[0].includes("'") && !hrefMatch[0].includes('"') ? "'" : '"';
    const replacedAttrs = attrs.replace(hrefMatch[0], `href=${quote}${rewritten}${quote}`);
    return `<a${replacedAttrs}>${inner}</a>`;
  });
  return { html: next, changes };
}

function sanitizeBlogHtml(value) {
  const openDivs = [];
  const source = String(value || '').replace(/&amp;nbsp;/gi, ' ');
  const html = source
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+=("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/<\/?([a-z0-9]+)(\b[^>]*)?>/gi, (full, name, rawAttrs = '') => {
      const tag = name.toLowerCase();
      if (tag === 'b') return full.startsWith('</') ? '</strong>' : '<strong>';
      if (tag === 'i') return full.startsWith('</') ? '</em>' : '<em>';
      if (!ALLOWED.has(tag)) return '';
      if (tag === 'br') return '<br>';
      if (tag === 'div') {
        if (full.startsWith('</')) {
          if (openDivs[openDivs.length - 1] === 'col') {
            openDivs.pop();
            return '';
          }
          if (!openDivs.pop()) return '';
          return '</div>';
        }
        const className = attr(rawAttrs || '', 'class');
        if (/\bphoto-masonry-col\b/.test(className)) {
          openDivs.push('col');
          return '';
        }
        if (/\bblog-rule\b/.test(className)) {
          openDivs.push('rule');
          return '<div class="blog-rule" role="separator">';
        }
        if (/\bblog-logo\b/.test(className)) {
          openDivs.push('logo');
          return `<div class="blog-logo ${logoSize(className)}">`;
        }
        const masonry = masonryColumns(className);
        if (masonry) {
          openDivs.push({ kind: 'masonry', columns: masonry });
          return `<div class="photo-masonry photo-masonry-${masonry}">`;
        }
        if (!/\bphoto-row\b/.test(className)) return '';
        const columns = /\bphoto-row-3\b/.test(className) ? 'photo-row-3' : (/\bphoto-row-2\b/.test(className) ? 'photo-row-2' : '');
        if (!columns) return '';
        const equal = /\bphoto-row-equal\b/.test(className) ? ' photo-row-equal' : '';
        openDivs.push({
          kind: 'row',
          slots: rowSlots(columns === 'photo-row-3' ? 3 : 2, Boolean(equal)),
          index: 0,
        });
        return `<div class="photo-row ${columns}${equal}">`;
      }
      if (full.startsWith('</')) return `</${tag}>`;
      const attrs = rawAttrs || '';
      if (tag === 'img' && openDivs.includes('logo')) return logoImage(attrs);
      if (tag === 'a') {
        const href = localHrefToRelative(attr(attrs, 'href'));
        if (!href || /^\s*javascript:/i.test(href)) return '<a>';
        const safe = href.startsWith('/') || /^https?:\/\//i.test(href) ? href : '';
        return safe ? `<a href="${escapeHtml(safe)}">` : '<a>';
      }
      if (tag === 'img') {
        const masonry = openMasonry(openDivs);
        if (masonry) return imageFromAttrs(attrs, masonry.columns === 1 ? null : MASONRY_SLOTS[masonry.columns]);
        const row = openRow(openDivs);
        if (!row) return imageFromAttrs(attrs);
        const slot = row.slots[Math.min(row.index, row.slots.length - 1)];
        row.index += 1;
        return imageFromAttrs(attrs, slot);
      }
      return `<${tag}>`;
    })
    .replace(/<figcaption>([\s\S]*?)<\/figcaption>/gi, (full, inner) => {
      const text = plain(inner, 300);
      return text ? `<figcaption>${escapeHtml(text)}</figcaption>` : '';
    })
    .replace(/<div class="blog-rule" role="separator">[\s\S]*?<\/div>/gi, '<div class="blog-rule" role="separator"></div>')
    .replace(/<div class="blog-logo blog-logo-(?:sm|md|lg)">\s*<\/div>/gi, '');
  return tightenBlogHtml(addHeadingIds(paragraphize(html)));
}

function tightenBlogHtml(html) {
  return String(html || '')
    .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/<p>\s*(?:<br\s*\/?>\s*)+/gi, '<p>')
    .replace(/(?:\s*<br\s*\/?>)+\s*<\/p>/gi, '</p>')
    .replace(/>(?:\s*<br\s*\/?>\s*)+</gi, '><')
    .replace(/^(?:\s|<br\s*\/?>)+/, '')
    .replace(/(?:\s|<br\s*\/?>)+$/, '');
}

function galleryThumbCount(post) {
  const count = Number(post && post.galleryCount);
  if (!Number.isFinite(count)) return 6;
  return Math.min(12, Math.max(1, Math.round(count)));
}

function postGalleryFilter(post) {
  return sitePaths.filterForSlug((post && (post.category || post.galleryCategory)) || '');
}

function selectPostGalleries(post, galleries) {
  if (!post || post.showGalleryBlock === false) return [];
  const count = galleryThumbCount(post);
  const stamped = sitePaths.withGallerySlugs(galleries || []);
  const pickedIds = (Array.isArray(post.galleryIds) ? post.galleryIds : []).map(String).filter(Boolean);
  if (pickedIds.length) {
    const order = new Map(pickedIds.map((id, index) => [id, index]));
    return stamped
      .filter((gallery) => order.has(String(gallery.id)) && gallery.published !== false)
      .sort((left, right) => (order.get(String(left.id)) || 0) - (order.get(String(right.id)) || 0))
      .slice(0, count);
  }
  const filter = postGalleryFilter(post);
  if (!filter) return [];
  return sitePaths.galleriesForFilter(galleries, filter.slug).slice(0, count);
}

module.exports = {
  escapeHtml,
  attr,
  responsiveImage,
  journalListImage,
  galleryCardDelivery,
  galleryCardImage,
  sanitizeBlogHtml,
  articleHeadings,
  normalizeEditorHtml,
  normalizeEditorHref,
  localHrefToRelative,
  rewriteStoredBlogHtml,
  galleryThumbCount,
  postGalleryFilter,
  selectPostGalleries,
};
